import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import { useNavigate } from "react-router-dom";
import { useEmployment } from "app/hooks/employment/useEmployment";
import { EMPLOYMENT } from "app/constants/apiEndPoints";
import { useDispatch } from "react-redux";

import {
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/employmentDetails";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import { employmentDetailsFromFour } from "app/utils/types";
import DynamicSecondEmployment from "./dynamicForms/DynamicSecondEmployment";
import { handleApiError } from "app/utils/handleApiError";
import { isValidPreviousEmployer } from "app/utils/helpers";

const SecondEmploymentDetails: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutate: submitEmployment, isPending } = useEmployment();
  const { formFour, ...others } = useSelector(
    (state: RootState) => state.employmentDetails.stepsData
  );
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
    register,
    reset,
    setError,
    watch,
  } = useForm<employmentDetailsFromFour>({
    defaultValues: {
      employerPhoneNumber: "",
      postCode: "",
      state: "",
      occupation: "",
    },
    shouldUnregister: true,
  });

  const employmentType = watch("employmentType");
  const onSubmit = (data: employmentDetailsFromFour) => {
    if (
      employmentType === "unemployed_or_retired" ||
      employmentType === "contractor" ||
      employmentType === "self-employed"
    ) {
      clearErrors([
        "startDate",
        "employerPhoneNumber",
        "postCode",
        "state",
        "street",
        "occupation",
      ]);
    }

    const APiformateData: Partial<any> = {
      employmentType: others?.formTwo?.employmentType,
      joiningDate: others?.formTwo?.joiningDate,
      employerName: others?.formOne?.employerName ?? "",
      ...(isValidPreviousEmployer(others?.formThree || {}) && {
        previousEmployer: {
          ...others.formThree,
          startDate: others?.formThree?.startDate,
          endDate: others?.formThree?.endDate,
        },
      }),
      secondJob: {
        ...data,
        startDate: data?.startDate,
      },
    };
    if (APiformateData?.previousEmployer?.secondjob) {
      delete APiformateData?.previousEmployer?.secondjob;
    }
    const formateData = {
      ...data,
      startDate: data?.startDate,
    };

    submitEmployment(
      {
        url: `${EMPLOYMENT}/${savedIds?.employmentID}/second`,
        data: APiformateData,
      },
      {
        onSuccess: (data: any) => {
          navigate(`/income-details`);
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );

    dispatch(
      addStepsData({
        ...others,
        formFour: formateData,
      })
    );
  };

  useEffect(() => {
    if (formFour) {
      reset(formFour);
    }
  }, [formFour, reset]);

  useEffect(() => {
    if (
      employmentType === "unemployed_or_retired" ||
      employmentType === "contractor" ||
      employmentType === "self-employed"
    ) {
      clearErrors([
        "startDate",
        "employerPhoneNumber",
        "postCode",
        "state",
        "street",
        "occupation",
      ]);
    }
  }, [employmentType, clearErrors]);

  return (
    <>
      <ProgressBar progress={100} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Second Employment Details" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <DynamicSecondEmployment
            control={control}
            errors={errors}
            register={register}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            occupation="occupation"
            startDate="startDate"
            employmentType="employmentType"
            employerPhoneNumber="employerPhoneNumber"
            addressStreetName="street"
            addressStateName="state"
            addressPostCodeName="postCode"
            selectedEmploymentType={employmentType}
          />

          <div className="flex justify-between my-10">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default SecondEmploymentDetails;
